import { supabase } from './supabase';
import { createStockItem, StockItem } from './stockItems';

export interface ProductOption {
  name: string;
  values: string[];
}

export interface ProductGroup {
  id: string;
  business_id: string;
  title: string;
  description: string | null;
  handle: string | null;
  product_type: string | null;
  vendor: string | null;
  tags: string[];
  status: 'active' | 'draft' | 'archived';
  options: ProductOption[];
  shopify_product_id: string | null;
  sync_source: string | null;
  is_deleted: boolean;
  created_at: string;
  updated_at: string;
}

export interface ProductGroupImage {
  id: string;
  product_group_id: string;
  url: string;
  position: number;
  alt: string | null;
  media_id?: string | null;
  shopify_image_id: string | null;
  created_at: string;
}

export interface CreateVariantInput {
  option_values: Record<string, string>;
  sku?: string | null;
  barcode?: string | null;
  price: number;
  acquisition_cost: number;
  compare_at_price?: number | null;
  quantity: number;
  reorder_point?: number | null;
  max_quantity?: number | null;
  // Index into CreateProductGroupInput.images for this variant's image (Shopify parity)
  image_index?: number | null;
}

export interface CreateProductGroupInput {
  business_id: string;
  title: string;
  description?: string | null;
  category_id: string | null;
  supplier_id?: string | null;
  vendor?: string | null;
  product_type?: string | null;
  unit?: string;
  status?: 'active' | 'draft' | 'archived';
  options: ProductOption[];
  variants: CreateVariantInput[];
  // Product media gallery — references to existing media library items.
  images?: { url: string; mediaId?: string | null }[];
}

/**
 * Build a human-readable variant name, e.g. "T-Shirt - M / Blue".
 * Falls back to the product title when there are no option values.
 */
function buildVariantName(title: string, optionValues: Record<string, string>): string {
  const parts = Object.values(optionValues).filter(Boolean);
  return parts.length > 0 ? `${title} - ${parts.join(' / ')}` : title;
}

/**
 * Create a product group (the parent "product") and one stock_item per variant.
 * Each variant reuses createStockItem so the inventory ledger (initial purchase
 * transaction) and one-time/quantity logic stay consistent with standalone items.
 */
export async function createProductGroupWithVariants(
  input: CreateProductGroupInput
): Promise<{ groupId: string; itemIds: string[] }> {
  const cleanedOptions = input.options
    .map(o => ({ name: o.name.trim(), values: o.values.map(v => v.trim()).filter(Boolean) }))
    .filter(o => o.name && o.values.length > 0);

  // 1. Create the parent product group
  const { data: group, error: groupError } = await supabase
    .from('product_groups')
    .insert({
      business_id: input.business_id,
      title: input.title,
      description: input.description ?? null,
      product_type: input.product_type ?? null,
      vendor: input.vendor ?? null,
      status: input.status ?? 'active',
      options: cleanedOptions,
      sync_source: 'app',
    })
    .select()
    .single();

  if (groupError) {
    console.error('Error creating product group:', groupError);
    throw new Error(groupError.message || 'Failed to create product');
  }

  // 2. Link the media gallery (already-uploaded library items) so variants can
  //    reference images by index.
  let galleryImages: ProductGroupImage[] = [];
  try {
    if (input.images && input.images.length > 0) {
      galleryImages = await linkProductGroupImages(group.id, input.images);
    }
  } catch (err) {
    // Group has no variants/items yet — safe to remove and bail
    await supabase.from('product_groups').delete().eq('id', group.id);
    throw err;
  }

  // 3. Create a stock_item per variant (resolving its assigned image, if any)
  const itemIds: string[] = [];
  try {
    for (const variant of input.variants) {
      const assignedImage =
        variant.image_index != null ? galleryImages[variant.image_index] : undefined;

      const created = await createStockItem({
        business_id: input.business_id,
        name: buildVariantName(input.title, variant.option_values),
        description: input.description ?? null,
        category_id: input.category_id,
        supplier_id: input.supplier_id ?? null,
        unit: input.unit || 'unit',
        sku: variant.sku ?? null,
        price: variant.price,
        is_sellable: true,
        acquisition_cost: variant.acquisition_cost,
        tax_rate: 0,
        quantity: variant.quantity,
        reorder_point: variant.reorder_point ?? 0,
        max_quantity: variant.max_quantity ?? 100,
        is_one_time: false,
        // Variant / grouping fields
        product_group_id: group.id,
        option_values: variant.option_values,
        barcode: variant.barcode ?? null,
        compare_at_price: variant.compare_at_price ?? null,
        image_url: assignedImage?.url ?? null,
        product_group_image_id: assignedImage?.id ?? null,
        sync_source: 'app',
      } as Omit<StockItem, 'id' | 'created_at' | 'updated_at'>);
      itemIds.push(created.id);
    }
  } catch (err) {
    // Roll back the group + any variants created so we don't leave an empty product.
    // Deleting the group cascades product_group_images.
    if (itemIds.length > 0) {
      await supabase.from('stock_items').delete().in('id', itemIds);
    }
    await supabase.from('product_groups').delete().eq('id', group.id);
    throw err;
  }

  return { groupId: group.id, itemIds };
}

/**
 * Convert an existing simple stock item into a variant group.
 *
 * The existing item becomes the FIRST variant — it keeps its id, stock level,
 * ledger history, and any invoice references; only its name, price/sku, and
 * grouping fields are updated. Every other variant is created as a new stock
 * item. Rolls back (restores the item, deletes new variants + group) on error.
 */
export async function convertItemToVariantGroup(input: {
  existingItem: StockItem;
  business_id: string;
  title: string;
  description?: string | null;
  category_id: string | null;
  supplier_id?: string | null;
  vendor?: string | null;
  unit?: string;
  options: ProductOption[];
  /** variants[0] maps onto the existing item. */
  variants: CreateVariantInput[];
  images?: { url: string; mediaId?: string | null }[];
}): Promise<{ groupId: string; itemIds: string[] }> {
  if (input.variants.length === 0) {
    throw new Error('Add at least one variant (an option with values).');
  }

  const cleanedOptions = input.options
    .map(o => ({ name: o.name.trim(), values: o.values.map(v => v.trim()).filter(Boolean) }))
    .filter(o => o.name && o.values.length > 0);

  // 1. Parent product group
  const { data: group, error: groupError } = await supabase
    .from('product_groups')
    .insert({
      business_id: input.business_id,
      title: input.title,
      description: input.description ?? null,
      vendor: input.vendor ?? null,
      status: 'active',
      options: cleanedOptions,
      sync_source: 'app',
    })
    .select()
    .single();
  if (groupError) throw new Error(groupError.message || 'Failed to create product');

  // 2. Gallery
  let galleryImages: ProductGroupImage[] = [];
  try {
    if (input.images && input.images.length > 0) {
      galleryImages = await linkProductGroupImages(group.id, input.images);
    }
  } catch (err) {
    await supabase.from('product_groups').delete().eq('id', group.id);
    throw err;
  }

  const existing = input.existingItem;
  const first = input.variants[0];
  const firstImage = first.image_index != null ? galleryImages[first.image_index] : undefined;
  const newItemIds: string[] = [];

  const rollback = async () => {
    // Restore the existing item's identity, remove new variants, drop the group.
    await supabase
      .from('stock_items')
      .update({
        name: existing.name,
        price: existing.price,
        sku: existing.sku ?? null,
        product_group_id: null,
        option_values: null,
        updated_at: new Date().toISOString(),
      })
      .eq('id', existing.id);
    if (newItemIds.length > 0) {
      await supabase.from('stock_items').delete().in('id', newItemIds);
    }
    await supabase.from('product_groups').delete().eq('id', group.id);
  };

  try {
    // 3. Existing item becomes the first variant (stock/history untouched)
    const { error: updateError } = await supabase
      .from('stock_items')
      .update({
        name: buildVariantName(input.title, first.option_values),
        description: input.description ?? null,
        category_id: input.category_id,
        supplier_id: input.supplier_id ?? null,
        unit: input.unit || existing.unit || 'unit',
        sku: first.sku ?? null,
        barcode: first.barcode ?? null,
        price: first.price,
        acquisition_cost: first.acquisition_cost,
        compare_at_price: first.compare_at_price ?? null,
        product_group_id: group.id,
        option_values: first.option_values,
        image_url: firstImage?.url ?? undefined,
        product_group_image_id: firstImage?.id ?? undefined,
        updated_at: new Date().toISOString(),
      })
      .eq('id', existing.id);
    if (updateError) throw updateError;

    // 4. Remaining variants are brand-new stock items
    for (const variant of input.variants.slice(1)) {
      const assignedImage =
        variant.image_index != null ? galleryImages[variant.image_index] : undefined;
      const created = await createStockItem({
        business_id: input.business_id,
        name: buildVariantName(input.title, variant.option_values),
        description: input.description ?? null,
        category_id: input.category_id,
        supplier_id: input.supplier_id ?? null,
        unit: input.unit || 'unit',
        sku: variant.sku ?? null,
        price: variant.price,
        is_sellable: true,
        acquisition_cost: variant.acquisition_cost,
        tax_rate: 0,
        quantity: variant.quantity,
        reorder_point: variant.reorder_point ?? 0,
        max_quantity: variant.max_quantity ?? 100,
        is_one_time: false,
        product_group_id: group.id,
        option_values: variant.option_values,
        barcode: variant.barcode ?? null,
        compare_at_price: variant.compare_at_price ?? null,
        image_url: assignedImage?.url ?? null,
        product_group_image_id: assignedImage?.id ?? null,
        sync_source: 'app',
      } as Omit<StockItem, 'id' | 'created_at' | 'updated_at'>);
      newItemIds.push(created.id);
    }
  } catch (err) {
    await rollback();
    throw err;
  }

  return { groupId: group.id, itemIds: [existing.id, ...newItemIds] };
}

/**
 * Link a set of existing media-library items to a product group as its ordered
 * gallery (product_group_images). No upload happens here — the media already
 * lives in the library. Returns the created image rows in order.
 */
export async function linkProductGroupImages(
  groupId: string,
  refs: { url: string; mediaId?: string | null }[],
  startPosition = 0
): Promise<ProductGroupImage[]> {
  if (refs.length === 0) return [];

  const rows = refs.map((ref, i) => ({
    product_group_id: groupId,
    url: ref.url,
    media_id: ref.mediaId ?? null,
    position: startPosition + i,
  }));

  const { data, error } = await supabase
    .from('product_group_images')
    .insert(rows)
    .select();

  if (error) {
    console.error('Error linking product group images:', error);
    throw error;
  }

  // Preserve the requested order (insert may not guarantee return order).
  return (data as ProductGroupImage[]).sort((a, b) => a.position - b.position);
}

/** Remove a single gallery image from a product group. */
export async function deleteProductGroupImage(imageId: string): Promise<void> {
  const { error } = await supabase
    .from('product_group_images')
    .delete()
    .eq('id', imageId);
  if (error) throw error;
}

/**
 * Upload a set of images for a product group to storage and record them
 * (ordered) in product_group_images. Returns the created image rows in order.
 */
export async function uploadProductGroupImages(
  groupId: string,
  files: File[]
): Promise<ProductGroupImage[]> {
  const created: ProductGroupImage[] = [];

  for (let i = 0; i < files.length; i++) {
    const file = files[i];
    const fileExt = file.name.split('.').pop();
    const fileName = `group-${groupId}-${i}-${Date.now()}.${fileExt}`;
    const filePath = `product-photos/${fileName}`;

    const { error: uploadError } = await supabase.storage
      .from('product-photos')
      .upload(filePath, file, { cacheControl: '3600', upsert: false });

    if (uploadError) {
      console.error('Error uploading product group image:', uploadError);
      throw uploadError;
    }

    const { data: { publicUrl } } = supabase.storage
      .from('product-photos')
      .getPublicUrl(filePath);

    const { data: imageRow, error: insertError } = await supabase
      .from('product_group_images')
      .insert({ product_group_id: groupId, url: publicUrl, position: i })
      .select()
      .single();

    if (insertError) {
      console.error('Error saving product group image record:', insertError);
      throw insertError;
    }

    created.push(imageRow as ProductGroupImage);
  }

  return created;
}

export interface ProductGroupWithImages extends ProductGroup {
  images: ProductGroupImage[];
}

/** Update editable product-group fields (title, description, vendor, status). */
export async function updateProductGroup(
  id: string,
  fields: Partial<Pick<ProductGroup, 'title' | 'description' | 'vendor' | 'status'>>
): Promise<void> {
  const { error } = await supabase
    .from('product_groups')
    .update({ ...fields, updated_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throw error;
}

/** Update editable per-variant fields on the underlying stock_item. */
export async function updateVariantFields(
  variantId: string,
  fields: Partial<Pick<StockItem, 'price' | 'sku' | 'barcode' | 'acquisition_cost'>>
): Promise<void> {
  const { error } = await supabase
    .from('stock_items')
    .update({ ...fields, updated_at: new Date().toISOString() })
    .eq('id', variantId);
  if (error) throw error;
}

/** Assign (or clear) a variant's image from the product's gallery. */
export async function setVariantImage(
  variantId: string,
  image: ProductGroupImage | null
): Promise<void> {
  const { error } = await supabase
    .from('stock_items')
    .update({
      image_url: image?.url ?? null,
      product_group_image_id: image?.id ?? null,
      updated_at: new Date().toISOString(),
    })
    .eq('id', variantId);
  if (error) throw error;
}

/** Set an authoritative absolute stock quantity via the ledger-safe RPC. */
export async function setVariantStockQuantity(
  variantId: string,
  quantity: number,
  note = 'Manual stock edit'
): Promise<void> {
  const { error } = await supabase.rpc('set_stock_quantity_absolute', {
    p_item_id: variantId,
    p_quantity: quantity,
    p_note: note,
  });
  if (error) throw error;
}

/**
 * Soft-delete an entire product group and all of its variant stock_items.
 * Variants are soft-deleted (not hard-deleted) to preserve sale/transaction history.
 */
export async function deleteProductGroup(groupId: string): Promise<void> {
  const nowIso = new Date().toISOString();
  const { error: variantErr } = await supabase
    .from('stock_items')
    .update({ is_deleted: true, updated_at: nowIso })
    .eq('product_group_id', groupId);
  if (variantErr) throw variantErr;

  const { error: groupErr } = await supabase
    .from('product_groups')
    .update({ is_deleted: true, updated_at: nowIso })
    .eq('id', groupId);
  if (groupErr) throw groupErr;
}

/**
 * Fetch all (non-deleted) product groups for a business with their ordered images.
 * Variants themselves live in stock_items (loaded separately) and are matched by
 * product_group_id on the client.
 */
export async function getProductGroupsWithImages(
  businessId: string
): Promise<ProductGroupWithImages[]> {
  const { data: groups, error } = await supabase
    .from('product_groups')
    .select('*')
    .eq('business_id', businessId)
    .eq('is_deleted', false)
    .order('created_at', { ascending: false });

  if (error) throw error;
  if (!groups || groups.length === 0) return [];

  const groupIds = groups.map(g => g.id);
  const { data: images, error: imgError } = await supabase
    .from('product_group_images')
    .select('*')
    .in('product_group_id', groupIds)
    .order('position', { ascending: true });

  if (imgError) throw imgError;

  const byGroup: Record<string, ProductGroupImage[]> = {};
  (images || []).forEach(img => {
    (byGroup[img.product_group_id] ||= []).push(img as ProductGroupImage);
  });

  return groups.map(g => ({ ...(g as ProductGroup), images: byGroup[g.id] || [] }));
}

/**
 * Generate the cartesian product of option values into variant combinations.
 * e.g. [{Size:[S,M]},{Color:[Blue]}] -> [{Size:S,Color:Blue},{Size:M,Color:Blue}]
 */
export function generateVariantCombinations(
  options: ProductOption[]
): Record<string, string>[] {
  const valid = options.filter(o => o.name.trim() && o.values.length > 0);
  if (valid.length === 0) return [];

  let combos: Record<string, string>[] = [{}];
  for (const option of valid) {
    const next: Record<string, string>[] = [];
    for (const combo of combos) {
      for (const value of option.values) {
        next.push({ ...combo, [option.name.trim()]: value });
      }
    }
    combos = next;
  }
  return combos;
}
